import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const empty = { email: "", password: "", name: "", role: "client_user", client_id: "" };

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [u, c] = await Promise.all([api.get("/admin/users"), api.get("/admin/clients")]);
      setUsers(u.data);
      setClients(c.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const body = { ...form };
      if (!body.client_id) body.client_id = null;
      await api.post("/admin/users", body);
      toast.success("User created");
      setEditing(null);
      setForm(empty);
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this user?")) return;
    await api.delete(`/admin/users/${id}`);
    load();
  };

  return (
    <div className="px-8 py-8 max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">Studio</div>
          <h1 className="font-display text-4xl tracking-tight mt-1">Users</h1>
        </div>
        <button onClick={() => { setEditing("new"); setForm(empty); }} data-testid="add-user-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-zinc-900 text-white text-sm hover:bg-zinc-700">
          <Plus className="h-4 w-4" /> New user
        </button>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-5 py-3">Email</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Role</th>
              <th className="text-left px-5 py-3">Client</th>
              <th className="text-right px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const c = clients.find(c => c.id === u.client_id);
              return (
                <tr key={u.id} className="border-t border-zinc-100" data-testid={`user-row-${u.id}`}>
                  <td className="px-5 py-4 font-medium">{u.email}</td>
                  <td className="px-5 py-4">{u.name}</td>
                  <td className="px-5 py-4">
                    <span className="text-xs px-2 py-1 rounded bg-zinc-100 text-zinc-700">{u.role}</span>
                  </td>
                  <td className="px-5 py-4 text-zinc-600">{c ? c.company_name : "—"}</td>
                  <td className="px-5 py-4 text-right">
                    {u.role !== "platform_admin" && (
                      <button onClick={() => del(u.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" data-testid={`del-user-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md rounded-xl p-6" onClick={e => e.stopPropagation()}>
            <div className="font-display text-xl mb-5">New user</div>
            <div className="space-y-4">
              {[["email", "Email", "email"], ["name", "Name", "text"], ["password", "Password", "password"]].map(([k, label, type]) => (
                <div key={k}>
                  <label className="block text-xs text-zinc-700 mb-1.5">{label}</label>
                  <input data-testid={`user-field-${k}`} type={type} value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900" />
                </div>
              ))}
              <div>
                <label className="block text-xs text-zinc-700 mb-1.5">Role</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900"
                  data-testid="user-field-role">
                  <option value="client_user">Client user</option>
                  <option value="platform_admin">Platform admin</option>
                </select>
              </div>
              {form.role === "client_user" && (
                <div>
                  <label className="block text-xs text-zinc-700 mb-1.5">Client</label>
                  <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-md border border-zinc-300 outline-none focus:border-zinc-900"
                    data-testid="user-field-client">
                    <option value="">— Select —</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-zinc-300 text-sm">Cancel</button>
              <button onClick={save} className="px-4 py-2 rounded-md bg-zinc-900 text-white text-sm" data-testid="user-save-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
